import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline } from './churchVessel';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './sintMariaPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function sintMariaFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Sint Maria lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const sintMariaChapel = () => ({ ...reading.chapel, apse: 'flat' as const });

export function sintMariaRangeFrame(
  plan: PlanFrame,
  part: (typeof reading.ranges)[number]
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

/** Grounded rooms only: neither the open court nor the Nes passage is a foundation. */
export function sintMariaFoundations(landmark: Landmark): Ring[] {
  const plan = sintMariaFrame(landmark);
  return [
    vesselOutline(sintMariaChapel()).map((p) => toLayout(plan, p)),
    ...reading.ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = sintMariaRangeFrame(plan, part);
      return (
        part.passage
          ? [
              { ...rect, u1: (part.passageU ?? 0) - part.passage / 2 },
              { ...rect, u0: (part.passageU ?? 0) + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export const sintMariaObstacles = sintMariaFoundations;

export function sintMariaFloors(landmark: Landmark) {
  const plan = sintMariaFrame(landmark);
  return [
    { ring: corners(reading.court), height: 0.025, garden: false },
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => ({
        ring: corners({
          u0: (p.passageU ?? 0) - p.passage / 2,
          u1: (p.passageU ?? 0) + p.passage / 2,
          v0: -p.width / 2,
          v1: p.width / 2,
        }).map((q) =>
          toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
        ),
        height: 0.025,
        garden: false,
      })),
    { ring: corners(reading.green), height: 0.04, garden: true },
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
